/**
 * ChatMessage - Modern AI chat message component with markdown support
 *
 * Features:
 * - Beautiful markdown rendering with syntax highlighting
 * - Streaming text support with smooth animations via @llm-ui/react
 * - Proper code block formatting (hides broken markdown during streaming)
 * - Typing indicators
 * - Avatar support
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/lib/stores/authStore';
import ReactMarkdown from 'react-markdown';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import ImageModal from '@/components/common/ImageModal';
import ManagedVideo from '@/components/common/ManagedVideo';
import CombatHighlightVideo from '@/components/common/CombatHighlightVideo';
import { Button } from '@/components/ui/button';
import { MessageCircle, Info } from 'lucide-react';
import { track } from '@/lib/analytics';
import { useAccountModalStore } from '@/lib/stores/accountModalStore';
import { DISCORD_INVITE_URL } from '@/lib/constants';
import { cn } from '@/lib/utils';
import { DEFAULT_PLAYER_AVATAR } from '@/lib/media';
import { renderSlashCommandText } from '@/lib/slash-command-display';
import {
  chatRemarkPlugins,
  chatRehypePlugins,
  chatMarkdownComponents,
  stripCodeBlocks,
  preprocessContent,
  type ChatMessageType,
} from '@/lib/markdown';
import { StreamingMarkdown } from './StreamingMarkdown';
import { ProgressiveReveal } from './ProgressiveReveal';
import { gameConnectionManager } from '@/lib/game-connection-manager';

export interface ChatMessageProps {
  id: string;
  content: string;
  senderName?: string;
  timestamp?: Date | string;
  type?: ChatMessageType;
  /**
   * Which chat surface this message lives in. Drives the per-author
   * typewriter gate in [[ProgressiveReveal]] — lobby + NPC author streams
   * are revealed as if a human is typing; game-mode messages render
   * instantly so combat round narration doesn't get throttled.
   */
  container: 'lobby' | 'game';
  isStreaming?: boolean;
  avatar?: string;
  avatarUrl?: string;
  avatarEmoji?: string;
  /** Optional full portrait for the sender — makes the sender avatar clickable to enlarge */
  senderPortraitUrl?: string;
  className?: string;
  /** Optional per-sender bubble color (hex) */
  bubbleColor?: string;
  /** Optional image attachment to display above the message */
  imageUrl?: string;
  /** True when this message is being fake-streamed from the queue */
  isQueuedReveal?: boolean;
  /** Called when ProgressiveReveal finishes typing this message */
  onRevealComplete?: () => void;
  /**
   * Kind-specific metadata. For `type === 'token-gate'`:
   *   - `action_kind`: discriminator for the inline button's behavior
   *     (currently only `'buyTokens'`).
   *   - `action_label`: button copy.
   * Mirrors mobile `ChatBubbleData.meta`.
   */
  meta?: Record<string, unknown>;
}

/**
 * Parse inline media markers from content.
 * Supports:
 * - [MODULE_IMAGE:url] - Module hero images
 * - [PARTY_IMAGE:url] - Party background images
 * - [NPC_HERO:url] - NPC hero videos/images
 * - [SCENE_IMAGE:url] - Scene/encounter images
 * - [CHAR_PORTRAIT:url] - Character portrait (squarish, top-cropped)
 * - [COMBAT_HIGHLIGHT:url] - Single-pass first-person combat clip with audio
 *
 * Returns the extracted media URL (if any), media type, and cleaned content.
 */
function parseInlineMedia(text: string): {
  mediaUrl: string | null;
  mediaType: 'image' | 'video' | 'portrait' | 'combat-highlight' | null;
  cleanedContent: string;
} {
  const mediaPattern =
    /\[(MODULE_IMAGE|PARTY_IMAGE|NPC_HERO|SCENE_IMAGE|CHAR_PORTRAIT|COMBAT_HIGHLIGHT):([^\]]+)\]/;
  const match = mediaPattern.exec(text);
  if (match) {
    const replacePattern =
      /\[(MODULE_IMAGE|PARTY_IMAGE|NPC_HERO|SCENE_IMAGE|CHAR_PORTRAIT|COMBAT_HIGHLIGHT):([^\]]+)\]/g;
    const url = match[2];
    const markerType = match[1];

    if (markerType === 'CHAR_PORTRAIT') {
      return {
        mediaUrl: url,
        mediaType: 'portrait',
        cleanedContent: text.replace(replacePattern, '').trim(),
      };
    }

    if (markerType === 'COMBAT_HIGHLIGHT') {
      return {
        mediaUrl: url,
        mediaType: 'combat-highlight',
        cleanedContent: text.replace(replacePattern, '').trim(),
      };
    }

    const isVideo = url.endsWith('.mp4') || url.endsWith('.webm');
    const mediaType: 'image' | 'video' = isVideo ? 'video' : 'image';

    return {
      mediaUrl: url,
      mediaType,
      cleanedContent: text.replace(replacePattern, '').trim(),
    };
  }
  return { mediaUrl: null, mediaType: null, cleanedContent: text };
}

/**
 * Strip a leading sender-name prefix from message content.
 *
 * Bubbles attributed to a character already display the name + avatar in the
 * header, so descriptions like "Aldric uses Potion of Healing" should render
 * as "Uses Potion of Healing" inside the bubble. Capitalize the next char
 * after stripping so the result reads as a proper sentence.
 */
export function stripSenderNamePrefix(content: string, senderName?: string): string {
  if (!senderName || !content) return content;

  // Match a leading sender-name token — optionally markdown-bold-wrapped —
  // followed by one or more separators (space, newline, or colon). The old
  // string-prefix check only handled a trailing SPACE, so narration that put
  // the name on its own line ("Kaelthar Emberplate\nPeers warily…") or after a
  // colon slipped through and doubled the name against the bubble header.
  const escaped = senderName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const prefixRe = new RegExp(`^(?:\\*\\*)?${escaped}(?:\\*\\*)?[\\s:]+`);
  const match = prefixRe.exec(content);
  if (!match) return content;

  const rest = content.slice(match[0].length);
  if (!rest) return '';
  const first = rest[0];
  if (first >= 'a' && first <= 'z') {
    return first.toUpperCase() + rest.slice(1);
  }
  return rest;
}

function hexToRgba(hex: string, alpha: number): string {
  const normalized = hex.replace('#', '');
  if (normalized.length !== 6 && normalized.length !== 3) return hex;

  const expand =
    normalized.length === 3
      ? normalized
          .split('')
          .map((c) => c + c)
          .join('')
      : normalized;

  const intVal = parseInt(expand, 16);
  const r = (intVal >> 16) & 255;
  const g = (intVal >> 8) & 255;
  const b = intVal & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

export function ChatMessage({
  id,
  content,
  senderName,
  // timestamp is available in props for future use (e.g., message timestamps)
  type = 'dm',
  isStreaming = false,
  avatar,
  avatarUrl,
  avatarEmoji,
  senderPortraitUrl,
  className,
  imageUrl: propImageUrl,
  bubbleColor,
  isQueuedReveal = false,
  onRevealComplete,
  meta,
  container,
}: ChatMessageProps) {
  const navigate = useNavigate();
  const openAccount = useAccountModalStore((s) => s.openModal);
  // Guests can't buy — the low-balance gate becomes a convert prompt for them.
  const isAnonymous = useAuthStore((s) => s.user?.isAnonymous ?? false);
  // Parse inline media markers from content (MODULE_IMAGE, PARTY_IMAGE, NPC_HERO)
  // During streaming, we still parse to remove markers from display (but don't extract media until streaming completes)
  const { mediaUrl, mediaType, cleanedContent } = React.useMemo(() => {
    if (!content) {
      return { mediaUrl: null, mediaType: null, cleanedContent: content };
    }
    const result = parseInlineMedia(content);

    // During live streaming, don't extract media (to avoid flicker), but still clean the content.
    // Queued reveal messages already have their full content — extract media normally.
    if (isStreaming && !isQueuedReveal) {
      return { mediaUrl: null, mediaType: null, cleanedContent: result.cleanedContent };
    }
    return result;
  }, [content, isStreaming, isQueuedReveal]);

  // Use prop imageUrl if provided, otherwise use parsed mediaUrl (for backwards compatibility)
  const imageUrl = propImageUrl || (mediaType === 'image' ? mediaUrl : null);
  const videoUrl = mediaType === 'video' ? mediaUrl : null;
  const combatHighlightUrl = mediaType === 'combat-highlight' ? mediaUrl : null;
  const combatHighlightGenerationId =
    typeof meta?.generation_id === 'string'
      ? meta.generation_id
      : id.startsWith('combat-highlight-')
        ? id.slice('combat-highlight-'.length)
        : null;
  const portraitUrl = mediaType === 'portrait' ? mediaUrl : null;
  // Use cleaned content (with media marker removed). Slash commands from the
  // user (e.g. `/invite Kael`) get rendered as natural-language prose
  // ("Invite Kael to the party") so the bubble doesn't show raw command
  // syntax. Gated on `type === 'user'` so DM/NPC/system messages that happen
  // to start with `/<word>` aren't silently rewritten.
  // Skip the strip during live streaming: content arrives token by token, and
  // applying the prefix match mid-stream would visibly delete several chars
  // ("Aldric u..." → "U...") at the moment the prefix completes. Queued-reveal
  // messages already hold full content, so stripping is safe and correct.
  const stripped =
    isStreaming && !isQueuedReveal
      ? (cleanedContent ?? '')
      : stripSenderNamePrefix(cleanedContent, senderName);
  const displayContent = type === 'user' ? renderSlashCommandText(stripped) : (stripped ?? '');

  // Discriminated union: image and video enlarge into the same ImageModal,
  // which renders a (muted) ManagedVideo for video or an <img> for image.
  const [enlargedAsset, setEnlargedAsset] = useState<{
    url: string;
    kind: 'image' | 'video';
  } | null>(null);

  // Don't render empty messages unless they are actively live-streaming (showing cursor).
  // For queued reveal (progressive fake-stream), empty content means nothing to reveal —
  // skip immediately to prevent empty GM bubbles with just a blinking cursor.
  if (!displayContent?.trim() && !imageUrl && !videoUrl && !combatHighlightUrl && !portraitUrl) {
    if (!isStreaming || isQueuedReveal) {
      return null;
    }
  }

  const getMessageStyle = () => {
    const baseClasses =
      'px-4 py-3 rounded-lg text-base leading-relaxed prose prose-base dark:prose-invert max-w-none';
    if (bubbleColor) {
      return {
        className: cn(baseClasses),
        style: {
          backgroundColor: hexToRgba(bubbleColor, 0.14),
        } as React.CSSProperties,
      };
    }
    switch (type) {
      case 'user':
        return { className: cn(baseClasses, 'bg-sky-500/10') };
      case 'whisper':
        return {
          className: cn(baseClasses, 'bg-violet-500/10 italic'),
        };
      case 'tavian':
        return { className: cn(baseClasses, 'bg-amber-500/10') };
      case 'aldric':
        return { className: cn(baseClasses, 'bg-blue-500/10') };
      case 'kira':
        return { className: cn(baseClasses, 'bg-purple-500/10') };
      case 'npc':
      case 'dm':
        return { className: cn(baseClasses, 'bg-accent/50') };
      case 'success':
      case 'party-join':
        return {
          className: cn(
            baseClasses,
            'bg-green-500/20 font-semibold text-green-700 dark:text-green-300'
          ),
        };
      case 'info':
        return {
          className: cn(
            baseClasses,
            'bg-blue-500/20 font-semibold text-blue-700 dark:text-blue-300'
          ),
        };
      case 'warning':
      case 'party-leave':
        return {
          className: cn(
            baseClasses,
            'bg-orange-500/20 font-medium text-orange-700 dark:text-orange-300'
          ),
        };
      case 'error':
        return {
          className: cn(baseClasses, 'bg-destructive/15 text-destructive'),
        };
      case 'system':
        return {
          className: cn(
            baseClasses,
            'bg-muted/50 font-semibold text-foreground font-system text-sm'
          ),
        };
      default:
        return { className: cn(baseClasses, 'bg-muted') };
    }
  };

  const getAvatarColor = () => {
    // Use consistent gray background for all avatars
    return 'bg-muted';
  };

  const isSystem = type === 'system' || type === 'error';
  const isBannerMessage = ['party-join', 'party-leave', 'success', 'info', 'warning'].includes(
    type || ''
  );

  const renderContent = (text: string) => (
    <ReactMarkdown
      remarkPlugins={chatRemarkPlugins}
      rehypePlugins={chatRehypePlugins}
      components={chatMarkdownComponents}
    >
      {preprocessContent(stripCodeBlocks(text))}
    </ReactMarkdown>
  );

  return (
    <div className={cn('flex gap-3 animate-in fade-in slide-in-from-bottom-2', className)}>
      {!isSystem && !isBannerMessage && (
        <Avatar
          className={cn('h-8 w-8 flex-shrink-0', senderPortraitUrl && 'cursor-pointer')}
          onClick={
            senderPortraitUrl
              ? () => setEnlargedAsset({ url: senderPortraitUrl, kind: 'image' })
              : undefined
          }
        >
          <AvatarImage
            src={
              // Image precedence: explicit avatarUrl/avatar > DEFAULT_PLAYER_AVATAR.
              // BUT only fall back to the default player image when there's no
              // avatarEmoji either — otherwise emoji-only NPCs (and any
              // non-user message converted to type='user' by gameStore for
              // CharacterActionMessage) would render the default player PNG
              // instead of their configured emoji. AvatarFallback below
              // handles the emoji rendering when AvatarImage src is undefined.
              avatarUrl ||
              avatar ||
              (type === 'user' && !avatarEmoji ? DEFAULT_PLAYER_AVATAR : undefined)
            }
            alt={senderName || 'User'}
          />
          <AvatarFallback className={cn(avatarEmoji ? 'text-lg' : 'text-xs', getAvatarColor())}>
            {avatarEmoji || senderName?.[0]?.toUpperCase() || '?'}
          </AvatarFallback>
        </Avatar>
      )}
      <div className={cn('flex-1 min-w-0 space-y-1', (isSystem || isBannerMessage) && 'w-full')}>
        {!isSystem && !isBannerMessage && senderName && (
          <div className="flex items-center gap-2 h-8">
            <span className="text-sm font-medium font-character">{senderName}</span>
          </div>
        )}
        {(() => {
          const style = getMessageStyle();
          return (
            <div className={style.className} style={style.style}>
              {/* Media attachment (rendered inside bubble, above text) */}
              {videoUrl && (
                <div className="-mx-4 -mt-3 mb-3 overflow-hidden rounded-t-lg aspect-video">
                  {/* Click to enlarge mirrors the portrait + image pattern. The
                      inline pauses while the enlarge modal is open (mirrors the
                      ScenePanel pattern; avoids running two decoders for the
                      same source). Videos are always muted — audio comes only
                      from the soundtrack engine. */}
                  <ManagedVideo
                    src={videoUrl}
                    paused={enlargedAsset?.url === videoUrl}
                    onClick={() => setEnlargedAsset({ url: videoUrl, kind: 'video' })}
                    className="w-full h-full object-cover cursor-pointer"
                  />
                </div>
              )}
              {combatHighlightUrl && (
                <div className="-mx-4 -mt-3 mb-3 overflow-hidden rounded-t-lg aspect-video">
                  <CombatHighlightVideo
                    src={combatHighlightUrl}
                    onReady={
                      combatHighlightGenerationId
                        ? () =>
                            gameConnectionManager.acknowledgeCombatHighlightRendered(
                              combatHighlightGenerationId
                            )
                        : undefined
                    }
                  />
                </div>
              )}
              {portraitUrl && (
                <div className="-mx-4 -mt-3 mb-3 overflow-hidden rounded-t-lg aspect-[4/3]">
                  <img
                    src={portraitUrl}
                    alt={senderName || 'Character'}
                    className="w-full h-full object-cover object-top cursor-pointer"
                    loading="lazy"
                    onClick={() => setEnlargedAsset({ url: portraitUrl, kind: 'image' })}
                  />
                </div>
              )}
              {imageUrl && (
                <div className="-mx-4 -mt-3 mb-3 overflow-hidden rounded-t-lg aspect-video">
                  <img
                    src={imageUrl}
                    alt="Scene"
                    className="w-full h-full object-cover cursor-pointer"
                    loading="lazy"
                    onClick={() => setEnlargedAsset({ url: imageUrl, kind: 'image' })}
                  />
                </div>
              )}
              {type === 'system' ? (
                <div className="flex items-start gap-2">
                  <Info className="h-4 w-4 shrink-0 mt-0.5" aria-hidden="true" />
                  <p className="m-0 whitespace-pre-wrap">{displayContent}</p>
                </div>
              ) : isSystem || isBannerMessage ? (
                <p className="m-0 whitespace-pre-wrap">{displayContent}</p>
              ) : type === 'token-gate' ? (
                <div className="space-y-3">
                  <p className="m-0 whitespace-pre-wrap">
                    {isAnonymous
                      ? 'Your free tokens are spent. Save your account to keep playing and buy more.'
                      : `${displayContent} The best way to earn more and reduce your Polymyth token spend is to play together with friends: all party members split the adventure costs.`}
                  </p>
                  {isAnonymous ? (
                    // Guests can't invite or buy — route them to the convert card.
                    <Button
                      className="bg-[hsl(var(--accent-gold))] text-white hover:bg-[hsl(var(--accent-gold))]/90"
                      onClick={() => navigate('/dashboard')}
                    >
                      Save Account
                    </Button>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {/* Invite friends is the highest-yield earn path (both sides
                          credited), so it leads. Buy Tokens stays for instant top-up. */}
                      <Button
                        className="bg-[hsl(var(--accent-gold))] text-white hover:bg-[hsl(var(--accent-gold))]/90"
                        onClick={() => {
                          track('invite_cta_click', { surface: 'chat_gate', low_balance: true });
                          openAccount('invite');
                        }}
                      >
                        Invite friends
                      </Button>
                      <Button variant="outline" onClick={() => navigate('/buy-tokens')}>
                        {(meta as { action_label?: string } | undefined)?.action_label ??
                          'Buy Tokens'}
                      </Button>
                    </div>
                  )}
                  {isAnonymous ? (
                    // Guests can't invite/buy or reach the Profile rewards, so keep
                    // their Discord-feedback earn path (mirrors the session-end toast).
                    <a
                      href={DISCORD_INVITE_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() =>
                        track('discord_feedback_cta_click', {
                          surface: 'chat_gate',
                          low_balance: true,
                        })
                      }
                      className="inline-flex items-center gap-1.5 text-xs font-medium text-foreground underline-offset-2 hover:text-foreground/80 hover:underline"
                    >
                      <MessageCircle className="h-3.5 w-3.5" />
                      Or share feedback on our Discord for bonus tokens
                    </a>
                  ) : (
                    // Signed-up: other earn paths (Discord + X rewards) live in the Profile tab.
                    <button
                      type="button"
                      onClick={() => {
                        track('earn_more_cta_click', { surface: 'chat_gate', low_balance: true });
                        openAccount('profile');
                      }}
                      className="inline-flex items-center gap-1.5 text-xs font-medium text-foreground underline-offset-2 hover:text-foreground/80 hover:underline"
                    >
                      <MessageCircle className="h-3.5 w-3.5" />
                      Additional ways to earn tokens
                    </button>
                  )}
                </div>
              ) : isQueuedReveal ? (
                // Dropped the `isStreaming` gate: typewriter NPCs are now
                // marked `isQueuedReveal: true` at NpcStreamStart and stay
                // marked until ProgressiveReveal catches up to the final
                // buffer (which happens after isStreaming flips false at
                // NpcStreamEnd). See `npc-events.ts` + `finishReveal`.
                <ProgressiveReveal
                  content={displayContent}
                  onComplete={onRevealComplete}
                  container={container}
                  type={type}
                  isStreaming={isStreaming}
                />
              ) : isStreaming ? (
                <StreamingMarkdown content={displayContent} isStreaming={isStreaming} />
              ) : (
                renderContent(displayContent)
              )}
            </div>
          );
        })()}
      </div>
      <ImageModal
        isOpen={enlargedAsset !== null}
        onClose={() => setEnlargedAsset(null)}
        imageUrl={enlargedAsset?.kind === 'image' ? enlargedAsset.url : undefined}
        videoUrl={enlargedAsset?.kind === 'video' ? enlargedAsset.url : undefined}
        altText={senderName || 'Chat image'}
      />
    </div>
  );
}
